import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { FACTION_IDS, FACTIONS } from "@frontier/content/factions";
import type { FactionId } from "@frontier/content/factions";
import {
  registerRequestSchema,
  loginRequestSchema,
} from "@frontier/protocol/auth-contracts";
import type {
  LoginRequest,
  RegisterRequest,
} from "@frontier/protocol/auth-contracts";
import type { Profile } from "@frontier/protocol/contracts";
import type { AuthSnapshot } from "./auth-session";
import { FactionInsignia } from "./FactionInsignia";
import { PilotNameForm } from "./PilotNameForm";
import { BackgroundMotionSettings } from "./BackgroundMotionSettings";
import type { BackgroundMotionControl } from "./use-background-motion";
import "./account.css";

export interface AccountPanelProps {
  onHelp?: () => void;
  backgroundMotion?: BackgroundMotionControl;
  profile: Profile | null;
  auth: AuthSnapshot;
  onLogin: (input: LoginRequest) => Promise<boolean>;
  onRegister: (input: RegisterRequest) => Promise<boolean>;
  onLogout: () => Promise<boolean>;
  onNewGuest: () => Promise<boolean>;
  onRetry: () => Promise<boolean>;
  onClose?: () => void;
  onChooseFaction?: (factionId: FactionId) => Promise<boolean>;
  onRename?: (name: string) => Promise<boolean>;
  busy?: boolean;
  platform?: { name?: string; onContinue?: () => Promise<void> };
}
export function FactionChoices({
  value,
  disabled,
  onChoose,
}: {
  value: FactionId | null;
  disabled: boolean;
  onChoose: (id: FactionId) => void;
}) {
  return (
    <fieldset className="faction-choice" disabled={disabled}>
      <legend>Фракция · равная сила · выбор один раз</legend>
      {FACTION_IDS.map((id) => (
        <button
          key={id}
          type="button"
          className="faction-card"
          style={{ color: FACTIONS[id].color }}
          data-faction={id}
          aria-pressed={value === id}
          onClick={() => onChoose(id)}
        >
          <FactionInsignia factionId={id} />
          <span>
            <b>{FACTIONS[id].name}</b>
            <small>{FACTIONS[id].description}</small>
          </span>
        </button>
      ))}
    </fieldset>
  );
}
export function AccountPanel(props: AccountPanelProps) {
  const { profile, auth } = props;
  const busy = auth.busy || props.busy === true;
  const [mode, setMode] = useState<"login" | "register">(
    profile && !profile.account ? "register" : "login",
  );
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [factionId, setFaction] = useState<FactionId | null>(null);
  const [error, setError] = useState("");
  const panel = useRef<HTMLElement>(null);
  const close = useRef(props.onClose);
  close.current = props.onClose;
  useEffect(() => {
    const previous = document.activeElement;
    panel.current?.focus();
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape" && close.current) {
        event.preventDefault();
        close.current();
      }
      if (event.key !== "Tab") return;
      const root = panel.current;
      if (!root) return;
      const controls = root.querySelectorAll<HTMLElement>(
        'button:not(:disabled),input:not(:disabled),summary,[tabindex="0"]',
      );
      const first = controls?.[0];
      const last = controls?.[controls.length - 1];
      if (!root.contains(document.activeElement)) {
        event.preventDefault();
        const target = event.shiftKey ? last : first;
        (target ?? root).focus();
        return;
      }
      if (
        event.shiftKey &&
        (document.activeElement === first ||
          document.activeElement === panel.current)
      ) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("keydown", key);
      if (previous instanceof HTMLElement && previous.isConnected)
        previous.focus();
    };
  }, []);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    const validationMessage =
      "Логин: 3–24 латинские буквы, цифры или _. Пароль: 10–128 символов. Для регистрации выбери фракцию.";
    let accepted = false;
    if (mode === "register") {
      const parsed = registerRequestSchema.safeParse({
        login,
        password,
        factionId,
      });
      if (!parsed.success) {
        setError(validationMessage);
        return;
      }
      setPassword("");
      accepted = await props.onRegister(parsed.data);
    } else {
      const parsed = loginRequestSchema.safeParse({ login, password });
      if (!parsed.success) {
        setError(validationMessage);
        return;
      }
      setPassword("");
      accepted = await props.onLogin(parsed.data);
    }
    setPassword("");
    if (accepted) props.onClose?.();
  };
  const faction = profile?.factionId ? FACTIONS[profile.factionId] : null;
  const platformName =
    profile?.account &&
    "provider" in profile.account &&
    profile.account.provider === "vkgames"
      ? "ВКонтакте"
      : (props.platform?.name ?? "VK Play");
  return (
    <div className="account-backdrop">
      <section
        ref={panel}
        tabIndex={-1}
        className="account-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-title"
      >
        <header className="account-heading">
          <div>
            <span>ФРОНТИР / ПИЛОТ</span>
            <h1 id="account-title">
              {profile?.account ? profile.name : "Аккаунт и фракция"}
            </h1>
          </div>
          {props.onClose && (
            <button
              type="button"
              aria-label="Закрыть аккаунт"
              onClick={props.onClose}
            >
              ×
            </button>
          )}
        </header>
        <div className="account-scroll">
          {profile && props.onRename && (
            <PilotNameForm
              key={profile.playerId}
              name={profile.name}
              disabled={busy || auth.status !== "ready" || !auth.session}
              onRename={props.onRename}
            />
          )}
          {profile &&
            !profile.factionId &&
            profile.tutorial?.route !== "training" &&
            props.onChooseFaction && (
              <section className="platform-faction">
                <FactionChoices
                  value={factionId}
                  disabled={busy}
                  onChoose={setFaction}
                />
                <button
                  className="account-submit"
                  disabled={!factionId || busy}
                  onClick={() => {
                    if (factionId)
                      void props
                        .onChooseFaction?.(factionId)
                        .then((accepted) => {
                          if (accepted) props.onClose?.();
                        });
                  }}
                >
                  {busy ? "Подтверждаем…" : "Присоединиться к фракции"}
                </button>
              </section>
            )}
          {profile?.account ? (
            <>
              <div className="account-identity">
                <FactionInsignia factionId={profile.factionId} />
                <div>
                  <b>{faction?.name ?? "Без фракции"}</b>
                  <p>
                    {"login" in profile.account
                      ? `Логин: ${profile.account.login}`
                      : `Вход через ${platformName}`}
                  </p>
                  <p>
                    {profile.factionId
                      ? "Фракция выбрана навсегда."
                      : profile.tutorial?.route === "training"
                        ? "Выбери портал фракции после обучения."
                        : "Выбери фракцию."}
                  </p>
                </div>
              </div>
            </>
          ) : profile && !profile.factionId ? (
            profile.tutorial?.route === "training" ? (
              <p className="account-preserve">
                Фракцию выберешь порталом в конце обучения.
              </p>
            ) : null
          ) : props.platform ? (
            <p className="account-preserve">Вход через {platformName}</p>
          ) : (
            <>
              {profile && (
                <p className="account-preserve">Гость · {profile.name}</p>
              )}
              <nav className="account-modes" aria-label="Вход или регистрация">
                <button
                  type="button"
                  disabled={busy}
                  aria-pressed={mode === "register"}
                  onClick={() => setMode("register")}
                >
                  Сохранить прогресс
                </button>
                <button
                  type="button"
                  disabled={busy}
                  aria-pressed={mode === "login"}
                  onClick={() => setMode("login")}
                >
                  Войти в аккаунт
                </button>
              </nav>
              {mode === "login" && profile && (
                <p>
                  Вход откроет другого сохранённого пилота. Прогресс гостя не
                  объединяется с аккаунтом.
                </p>
              )}
              <form
                className="account-form"
                onSubmit={(event) => void submit(event)}
              >
                <div className="account-credentials">
                  <label>
                    Логин
                    <input
                      name="login"
                      autoComplete="username"
                      autoCapitalize="none"
                      spellCheck={false}
                      minLength={3}
                      maxLength={24}
                      pattern="[A-Za-z0-9_]+"
                      value={login}
                      onChange={(event) => setLogin(event.target.value)}
                      disabled={busy}
                      required
                    />
                  </label>
                  <label>
                    Пароль
                    <input
                      name="password"
                      type="password"
                      autoComplete={
                        mode === "register"
                          ? "new-password"
                          : "current-password"
                      }
                      minLength={10}
                      maxLength={128}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      disabled={busy}
                      required
                    />
                  </label>
                  <small>Логин без пробелов, пароль от 10 символов.</small>
                </div>
                {mode === "register" && (
                  <FactionChoices
                    value={factionId}
                    disabled={busy}
                    onChoose={setFaction}
                  />
                )}
                <button
                  className="account-submit"
                  type="submit"
                  disabled={busy}
                >
                  {busy
                    ? "Подтверждаем…"
                    : mode === "register"
                      ? "Зарегистрировать этого пилота"
                      : "Войти"}
                </button>
              </form>
            </>
          )}
          {profile?.prestige?.title && (
            <section className="account-prestige" aria-label="Престиж пилота">
              <svg viewBox="0 0 32 32" aria-hidden="true">
                <path d="m16 2 4 9 10 1-7 7 2 11-9-5-9 5 2-11-7-7 10-1Z" />
              </svg>
              <div>
                <small>ПРЕСТИЖ ФРОНТИРА</small>
                <b>{profile.prestige.title}</b>
                <span>
                  Захвачено ретрансляторов: {profile.prestige.relayCaptures}
                </span>
                <small>Постоянное отличие · без бонуса к силе</small>
              </div>
            </section>
          )}
          {(error || auth.error) && (
            <p className="account-error" role="alert">
              {error || auth.error}
            </p>
          )}
          <div className="account-actions">
            {props.platform && !auth.session && (
              <>
                {props.platform.onContinue && (
                  <button
                    disabled={busy}
                    onClick={() => {
                      setError("");
                      void props.platform
                        ?.onContinue?.()
                        .catch((failure) =>
                          setError(
                            failure instanceof Error
                              ? failure.message
                              : `Не удалось открыть ${platformName}.`,
                          ),
                        );
                    }}
                  >
                    Продолжить в {platformName}
                  </button>
                )}
                {auth.retryAvailable && (
                  <button disabled={busy} onClick={() => void props.onRetry()}>
                    Повторить подключение
                  </button>
                )}
              </>
            )}
            {!props.platform && !auth.session && auth.status !== "loading" && (
              <>
                {auth.retryAvailable && (
                  <button disabled={busy} onClick={() => void props.onRetry()}>
                    Повторить подключение
                  </button>
                )}
                <button
                  disabled={busy}
                  onClick={() =>
                    void props.onNewGuest().then((accepted) => {
                      if (accepted) props.onClose?.();
                    })
                  }
                >
                  Начать новым гостем
                </button>
              </>
            )}
            {!props.platform && (profile || auth.logoutPending) && (
              <button disabled={busy} onClick={() => void props.onLogout()}>
                {auth.logoutPending
                  ? "Повторить выход"
                  : "Выйти на этом устройстве"}
              </button>
            )}
            {props.onHelp && <button onClick={props.onHelp}>Помощь</button>}
          </div>
          {props.backgroundMotion && (
            <BackgroundMotionSettings motion={props.backgroundMotion} />
          )}
        </div>
      </section>
    </div>
  );
}
